<template>
  <div class="customization-backdrop" @click.self="emit('close')">
    <div class="customization-panel" :class="{ 'dragging-from-reservoir': isDraggingFromReservoir, 'ctrl-dragging': isCtrlDragging }">
      <div class="panel-header">
        <h3>Customise Toolbar</h3>
        <button @click="emit('close')" aria-label="Close" title="Close"><X :size="16" /></button>
      </div>

      <div class="columns">
        <!-- Available items (reservoir) — always shows all items; drag out = copy -->
        <div class="column">
          <div class="column-header">
            <span>Available items</span>
          </div>
          <draggable
            v-model="reservoirList"
            :group="{ name: 'toolbar', pull: 'clone', put: true }"
            item-key="id"
            class="drag-list reservoir-list"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :sort="false"
            :animation="200"
            :clone="cloneReservoirItem"
            @start="onDragStart(true)"
            @end="onDragEnd"
            @change="onReservoirChange"
          >
            <template #item="{ element }">
              <div class="drag-item reservoir-item">
                <span class="item-icon"><component :is="TOOLBAR_ITEM_ICONS[element.id]" :size="14" /></span>
                <span class="item-label">{{ TOOLBAR_ITEM_LABELS[element.id] }}</span>
                <button class="quick-add no-drag" @click="quickAddToBottom(element.id)" title="Add to bottom bar" aria-label="Add to bottom bar">
                  <PlusCircle :size="14" />
                </button>
              </div>
            </template>
          </draggable>
        </div>

        <!-- Top Bar -->
        <div class="column">
          <div class="column-header">
            <span>Top bar</span>
            <select v-model="toolbarStore.topBarStyle" class="style-picker" aria-label="Top bar style" title="Bar style">
              <option value="icon-only">Icon only</option>
              <option value="label-only">Label only</option>
              <option value="icon+label">Icon + label</option>
            </select>
          </div>
          <draggable
            v-model="topList"
            :group="barGroupConfig"
            item-key="key"
            class="drag-list"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :animation="200"
            :clone="cloneBarItem"
            @start="onDragStart(false)"
            @end="onDragEnd"
            @change="onTopChange"
          >
            <template #item="{ element }">
              <div class="drag-item">
                <span class="drag-handle" title="Drag to reorder"><GripVertical :size="14" /></span>
                <span class="item-icon"><component :is="TOOLBAR_ITEM_ICONS[element.id]" :size="14" /></span>
                <span class="item-label">{{ TOOLBAR_ITEM_LABELS[element.id] }}</span>
                <button class="remove-btn no-drag" @click="removeItem(element.key, 'top')" title="Remove from bar" aria-label="Remove from top bar">
                  <X :size="12" />
                </button>
              </div>
            </template>
          </draggable>
          <div class="column-empty" v-if="topList.length === 0">Empty — drag items here</div>
        </div>

        <!-- Bottom Bar -->
        <div class="column">
          <div class="column-header">
            <span>Bottom bar</span>
            <select v-model="toolbarStore.bottomBarStyle" class="style-picker" aria-label="Bottom bar style" title="Bar style">
              <option value="icon-only">Icon only</option>
              <option value="label-only">Label only</option>
              <option value="icon+label">Icon + label</option>
            </select>
          </div>
          <draggable
            v-model="bottomList"
            :group="barGroupConfig"
            item-key="key"
            class="drag-list"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :animation="200"
            :clone="cloneBarItem"
            @start="onDragStart(false)"
            @end="onDragEnd"
            @change="onBottomChange"
          >
            <template #item="{ element }">
              <div class="drag-item">
                <span class="drag-handle" title="Drag to reorder"><GripVertical :size="14" /></span>
                <span class="item-icon"><component :is="TOOLBAR_ITEM_ICONS[element.id]" :size="14" /></span>
                <span class="item-label">{{ TOOLBAR_ITEM_LABELS[element.id] }}</span>
                <button class="remove-btn no-drag" @click="removeItem(element.key, 'bottom')" title="Remove from bar" aria-label="Remove from bottom bar">
                  <X :size="12" />
                </button>
              </div>
            </template>
          </draggable>
          <div class="column-empty" v-if="bottomList.length === 0">Empty</div>
        </div>
        <!-- Settings Panel bar -->
        <div class="column">
          <div class="column-header">
            <span>Settings bar</span>
            <select v-model="toolbarStore.settingsBarStyle" class="style-picker" aria-label="Settings bar style" title="Bar style">
              <option value="icon-only">Icon only</option>
              <option value="label-only">Label only</option>
              <option value="icon+label">Icon + label</option>
            </select>
          </div>
          <div class="column-note">Items shown in the settings overlay header. Order &amp; selection only.</div>
          <draggable
            v-model="settingsBarList"
            :group="barGroupConfig"
            item-key="key"
            class="drag-list"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :animation="200"
            :clone="cloneBarItem"
            @start="onDragStart(false)"
            @end="onDragEnd"
            @change="onSettingsBarChange"
          >
            <template #item="{ element }">
              <div class="drag-item">
                <span class="drag-handle" title="Drag to reorder"><GripVertical :size="14" /></span>
                <span class="item-icon"><component :is="TOOLBAR_ITEM_ICONS[element.id]" :size="14" /></span>
                <span class="item-label">{{ TOOLBAR_ITEM_LABELS[element.id] }}</span>
                <button class="remove-btn no-drag" @click="removeSettingsBarItem(element.key)" title="Remove from settings bar" aria-label="Remove from settings bar">
                  <X :size="12" />
                </button>
              </div>
            </template>
          </draggable>
          <div class="column-empty" v-if="settingsBarList.length === 0">Empty</div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="history-btns">
          <button :disabled="!canUndo" @click="undo()" title="Undo (⌘Z / Ctrl+Z)" aria-label="Undo">
            <Undo2 :size="14" />
          </button>
          <button :disabled="!canRedo" @click="redo()" title="Redo (⌘⇧Z / Ctrl+Y)" aria-label="Redo">
            <Redo2 :size="14" />
          </button>
        </div>
        <button class="reset-btn" @click="doReset()">Reset to default</button>
      </div>

      <!-- Warning when settings item is not in any bar -->
      <div class="settings-hidden-warning" v-if="showSettingsHiddenWarning">
        <Settings :size="14" />
        The settings button is not in any bar.<br>
        You can always open settings with <kbd>S</kbd> or <kbd>⌘,</kbd> / <kbd>Ctrl+,</kbd>.
        <button @click="showSettingsHiddenWarning = false" aria-label="Dismiss"><X :size="12" /></button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import draggable from "vuedraggable";
import { GripVertical, PlusCircle, Redo2, Settings, Undo2, X } from "lucide-vue-next";
import { useManualRefHistory, useEventListener } from "@vueuse/core";
import {
  useToolbarStore,
  TOOLBAR_ITEM_ICONS,
  TOOLBAR_ITEM_LABELS,
  ALL_TOOLBAR_ITEM_IDS,
} from "@/stores/toolbar";
import type { BarItemEntry, ToolbarItemId } from "@/stores/toolbar";

const emit = defineEmits<{ (e: "close"): void }>();
const toolbarStore = useToolbarStore();

/** The reservoir always lists all possible items (never changes). */
const reservoirList = ref(ALL_TOOLBAR_ITEM_IDS.map((id) => ({ id })));

/** Local copies for vuedraggable v-model */
const topList = ref<BarItemEntry[]>([...toolbarStore.topItems]);
const bottomList = ref<BarItemEntry[]>([...toolbarStore.bottomItems]);
const settingsBarList = ref<BarItemEntry[]>([...toolbarStore.settingsBarItems]);

function syncListsFromStore() {
  topList.value = [...toolbarStore.topItems];
  bottomList.value = [...toolbarStore.bottomItems];
  settingsBarList.value = [...toolbarStore.settingsBarItems];
}

// ── Undo / Redo ─────────────────────────────────────────────────────────────

type BarSnapshot = { top: BarItemEntry[]; bottom: BarItemEntry[]; settingsBar: BarItemEntry[] };

function deepClone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }

function captureSnapshot(): BarSnapshot {
  return deepClone({ top: topList.value, bottom: bottomList.value, settingsBar: settingsBarList.value });
}

const historyState = ref<BarSnapshot>(captureSnapshot());

const {
  commit: histCommit,
  undo: histUndo,
  redo: histRedo,
  canUndo,
  canRedo,
} = useManualRefHistory(historyState, { clone: deepClone });

/** True while restoring from history, to skip the store→list sync watcher. */
let _restoringHistory = false;

// Watch for undo/redo changes to historyState and apply them to the lists.
// flush:'sync' ensures the flag is cleared atomically so rapid undo→redo works.
watch(historyState, () => {
  if (!_restoringHistory) return;
  const s = historyState.value;
  topList.value = [...s.top];
  bottomList.value = [...s.bottom];
  settingsBarList.value = [...s.settingsBar];
  toolbarStore.reorder("top", topList.value);
  toolbarStore.reorder("bottom", bottomList.value);
  toolbarStore.reorder("settings-bar", settingsBarList.value);
  _restoringHistory = false;
}, { deep: true, flush: "sync" });

/**
 * Call AFTER applying a change to the lists.
 *
 * vueuse's commit() pushes the internal `last` record to the undo stack
 * and creates a new `last` from the current source value. So we must
 * update historyState (= source) to the NEW state BEFORE calling commit(),
 * so that:
 *   - undo stack gets the OLD last (pre-change snapshot)  ← "before"
 *   - new last captures the NEW state (post-change)       ← "after"
 * This means undo correctly restores the pre-change state, and redo
 * correctly reapplies the post-change state.
 */
function recordChange() {
  historyState.value = captureSnapshot(); // Capture AFTER change (sets new source value)
  histCommit();                           // Pushes old last → undo; new last = after-change
}

function undo() {
  if (!canUndo.value) return;
  _restoringHistory = true;
  histUndo(); // sets historyState.value → watch applies to lists
}

function redo() {
  if (!canRedo.value) return;
  _restoringHistory = true;
  histRedo(); // sets historyState.value → watch applies to lists
}

// Keyboard shortcuts and Ctrl tracking (only active while panel is mounted)
useEventListener(document, "keydown", (event: KeyboardEvent) => {
  // Track Ctrl key for copy mode during drag
  if (event.ctrlKey) {
    isCtrlDragging.value = true;
  }

  const isMac = navigator.platform.startsWith("Mac");
  const mod = isMac ? event.metaKey : event.ctrlKey;
  if (!mod) return;
  if (event.key === "z" && !event.shiftKey) {
    event.preventDefault();
    undo();
  } else if ((event.key === "z" && event.shiftKey) || event.key === "y") {
    event.preventDefault();
    redo();
  }
});

useEventListener(document, "keyup", (event: KeyboardEvent) => {
  // Track Ctrl key release for copy mode
  if (event.key === "Control") {
    isCtrlDragging.value = false;
  }
});

// Keep local lists in sync when store changes from outside
watch(() => toolbarStore.topItems, () => { if (!_restoringHistory) syncListsFromStore(); }, { deep: true });
watch(() => toolbarStore.bottomItems, () => { if (!_restoringHistory) syncListsFromStore(); }, { deep: true });
watch(() => toolbarStore.settingsBarItems, () => { if (!_restoringHistory) syncListsFromStore(); }, { deep: true });

/** Called by vuedraggable :clone to produce a fresh BarItemEntry from a reservoir element. */
function cloneReservoirItem(source: { id: ToolbarItemId }): BarItemEntry {
  return { id: source.id, key: crypto.randomUUID() };
}

/** Called by vuedraggable :clone to produce a fresh BarItemEntry when copying with Ctrl+drag. */
function cloneBarItem(source: BarItemEntry): BarItemEntry {
  return { id: source.id, key: crypto.randomUUID() };
}

function onTopChange() {
  dragChangedList = true;
  toolbarStore.reorder("top", topList.value);
  recordChange();
}

function onBottomChange() {
  dragChangedList = true;
  toolbarStore.reorder("bottom", bottomList.value);
  recordChange();
}

function onSettingsBarChange() {
  dragChangedList = true;
  toolbarStore.reorder("settings-bar", settingsBarList.value);
  recordChange();
}

function removeItem(key: string, slot: "top" | "bottom") {
  if (slot === "top") {
    topList.value = topList.value.filter((i) => i.key !== key);
  } else {
    bottomList.value = bottomList.value.filter((i) => i.key !== key);
  }
  toolbarStore.reorder("top", topList.value);
  toolbarStore.reorder("bottom", bottomList.value);
  recordChange();
}

function removeSettingsBarItem(key: string) {
  settingsBarList.value = settingsBarList.value.filter((i) => i.key !== key);
  toolbarStore.reorder("settings-bar", settingsBarList.value);
  recordChange();
}

function quickAddToBottom(id: ToolbarItemId) {
  toolbarStore.addItem(id, "bottom");
  bottomList.value = [...toolbarStore.bottomItems];
  recordChange();
}

function doReset() {
  toolbarStore.resetToDefault();
  syncListsFromStore();
  recordChange();
}

/** Track whether the current drag originated from the reservoir (for copy cursor). */
const isDraggingFromReservoir = ref(false);

/** Whether the current drag resulted in a successful placement (change event fired). */
let dragChangedList = false;

/** Track Ctrl key state during drag for copy mode. */
const isCtrlDragging = ref(false);

function onDragStart(fromReservoir: boolean) {
  isDraggingFromReservoir.value = fromReservoir;
  dragChangedList = false;
  isCtrlDragging.value = false;
}

function onDragEnd(event: DragEvent & { oldIndex: number; newIndex: number; from: HTMLElement; to: HTMLElement; item: HTMLElement }) {
  isDraggingFromReservoir.value = false;
  // Detect failed drop: item returned to the same position in the same container
  const returnedToOrigin =
    !dragChangedList &&
    event.from === event.to &&
    event.newIndex === event.oldIndex;
  if (returnedToOrigin) {
    const el = event.item;
    el.classList.add("drag-returned");
    const cleanup = () => el.classList.remove("drag-returned");
    el.addEventListener("animationend", cleanup, { once: true });
  }
}

function onReservoirChange(event: any) {
  // If an item was dropped INTO the reservoir from a bar, remove it from the bar.
  // The reservoir accepts items but doesn't keep them — this implements the delete behavior.
  if (event.added) {
    const addedItem = event.added.element;
    // Remove from all bars (it will only be in one)
    topList.value = topList.value.filter((i) => i.key !== addedItem.key);
    bottomList.value = bottomList.value.filter((i) => i.key !== addedItem.key);
    settingsBarList.value = settingsBarList.value.filter((i) => i.key !== addedItem.key);

    // Clear the reservoir list to its original state (always full)
    reservoirList.value = ALL_TOOLBAR_ITEM_IDS.map((id) => ({ id }));

    // Update stores for all bars
    toolbarStore.reorder("top", topList.value);
    toolbarStore.reorder("bottom", bottomList.value);
    toolbarStore.reorder("settings-bar", settingsBarList.value);
    recordChange();
  }
}

// Computed group configuration for bar lists with copy mode support
const barGroupConfig = computed(() => ({
  name: 'toolbar',
  pull: isCtrlDragging.value ? 'clone' : true,
  put: true,
}));

// Warning when settings item is not in any bar
const showSettingsHiddenWarning = ref(false);
const settingsInAnyBar = computed(
  () =>
    topList.value.some((i) => i.id === "settings") ||
    bottomList.value.some((i) => i.id === "settings")
);
watch(settingsInAnyBar, (isInBar) => {
  if (!isInBar) showSettingsHiddenWarning.value = true;
});
</script>

<style scoped lang="scss">
.customization-backdrop {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customization-panel {
  background: var(--bg-surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  width: min(960px, 97vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Copy cursor on bar columns while dragging from reservoir */
  &.dragging-from-reservoir .drag-list:not(.reservoir-list) {
    cursor: copy;
  }

  /* Delete cursor on reservoir while dragging a bar item over it */
  &:not(.dragging-from-reservoir) .reservoir-list {
    cursor: no-drop;
  }
}

/* Show copy cursor on all draggable bars when Ctrl+drag is active and not dragging from reservoir */
.customization-panel.ctrl-dragging:not(.dragging-from-reservoir) .drag-list:not(.reservoir-list) {
  cursor: copy;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);

  h3 { margin: 0; font-size: 1rem; }

  button {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    &:hover { color: var(--accent); }
  }
}

.columns {
  display: flex;
  gap: 12px;
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}

.column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-accent);
}

.style-picker {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 0.7rem;
  font-weight: 400;
  text-transform: none;
  padding: 2px 4px;
  cursor: pointer;
  &:hover { border-color: var(--accent); }
}

.column-note {
  font-size: 0.75rem;
  color: var(--text-secondary, var(--text-primary));
  opacity: 0.75;
  line-height: 1.4;
}

.drag-list {
  min-height: 60px;
  background: var(--bg-elevated);
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reservoir-list {
  /* Reservoir is not a drop target — keep its style neutral */
  border-style: solid;
  opacity: 0.85;
  max-height: 400px;
  overflow-y: auto;
}

.drag-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  cursor: grab;

  &:active { cursor: grabbing; }
  &:hover { border-color: var(--accent); }
}

.reservoir-item {
  /* Copy cursor only shows when actually dragging FROM reservoir, not on hover */
  cursor: grab;
  &:active { cursor: grabbing; }
}

.drag-handle {
  color: var(--text-secondary, var(--text-primary));
  opacity: 0.5;
  pointer-events: none; /* visual-only; whole item is the drag target */
}

.item-icon {
  display: flex;
  align-items: center;
  opacity: 0.7;
  flex-shrink: 0;
}

.item-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quick-add,
.remove-btn {
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0;
  opacity: 0.5;
  flex-shrink: 0;
  &:hover { opacity: 1; color: var(--accent); }
}

.remove-btn:hover {
  color: var(--danger, #e55);
}

.drag-ghost {
  opacity: 0.4;
  background: var(--accent);
}

.drag-chosen {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  opacity: 0.9;
}

@keyframes drag-return {
  0%   { transform: translateY(-6px) scale(1.03); opacity: 0.6; }
  60%  { transform: translateY(2px) scale(0.99); opacity: 0.9; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}

.drag-returned {
  animation: drag-return 0.25s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .drag-item { transition: none; }
  .drag-returned { animation: none; }
}

.column-empty {
  padding: 12px 8px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-secondary, var(--text-primary));
  opacity: 0.6;
}

.panel-footer {
  padding: 10px 16px;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .history-btns {
    display: flex;
    gap: 4px;

    button {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      color: var(--text-primary);
      cursor: pointer;
      padding: 5px 8px;
      display: flex;
      align-items: center;
      &:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
      &:disabled { opacity: 0.35; cursor: not-allowed; }
    }
  }

  .reset-btn {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    cursor: pointer;
    padding: 6px 14px;
    font-size: 0.85rem;
    margin-left: auto;
    &:hover { border-color: var(--accent); color: var(--accent); }
  }
}

/* Mobile: fullscreen panel, stacked columns */
@media (max-width: 640px) {
  .customization-backdrop {
    align-items: flex-end;
  }

  .customization-panel {
    width: 100vw;
    max-height: 92dvh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    border-bottom: none;
  }

  .columns {
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .column {
    min-width: 0;
    width: 100%;
  }

  .drag-list {
    min-height: 44px;
  }
}

.settings-hidden-warning {
  margin: 0 16px 12px;
  padding: 10px 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;

  kbd {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 0.8em;
  }

  button {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-primary);
    &:hover { color: var(--accent); }
  }
}
</style>
